import { getConsentState } from "./consent.js";

const STORAGE_KEY = "mon5_attribution";
const SCHEMA_VERSION = 2;
const DEFAULT_TTL_DAYS = 90;
const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "fbclid"];
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const CAMPAIGN_PARAMS = [...CLICK_ID_PARAMS, ...UTM_PARAMS];

function isBrowser() {
  return typeof window !== "undefined";
}

function getTtlMs() {
  const configured = Number(process.env.GATSBY_ATTRIBUTION_TTL_DAYS);
  const days = Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_TTL_DAYS;
  return days * 24 * 60 * 60 * 1000;
}

// Fallback in memoria: consente di completare un percorso SPA quando il browser
// blocca localStorage. Viene comunque invalidato con la stessa scadenza.
let memoryStore = {};

function emptyState() {
  return { schema_version: SCHEMA_VERSION };
}

function readRaw() {
  if (!isBrowser()) return {};
  let stored = {};
  try {
    stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    // localStorage non leggibile: resta quanto catturato in questa sessione.
  }
  return { ...stored, ...memoryStore };
}

function isValidTouch(touch) {
  return !!(
    touch &&
    typeof touch === "object" &&
    typeof touch.captured_at === "number" &&
    Date.now() - touch.captured_at <= getTtlMs()
  );
}

function readStored() {
  const stored = readRaw();
  if (stored.schema_version !== SCHEMA_VERSION) return emptyState();
  // `captured_at` viene riscritto a ogni visita: il record intero scade dopo il
  // TTL di inattivita', non solo i due touch.
  if (typeof stored.captured_at === "number" && Date.now() - stored.captured_at > getTtlMs()) {
    return emptyState();
  }
  const next = { ...stored };
  if (next.first_touch && !isValidTouch(next.first_touch)) delete next.first_touch;
  if (next.last_non_direct_touch && !isValidTouch(next.last_non_direct_touch)) {
    delete next.last_non_direct_touch;
  }
  if (next.landing_page && typeof next.landing_page !== "string") delete next.landing_page;
  return next;
}

function collectCampaignParams(search, { allowAnalytics, allowMarketing }) {
  const params = new URLSearchParams(search || "");
  const found = {};
  for (const key of CAMPAIGN_PARAMS) {
    if (CLICK_ID_PARAMS.includes(key) && !allowMarketing) continue;
    if (UTM_PARAMS.includes(key) && !allowAnalytics && !allowMarketing) continue;
    const value = params.get(key);
    if (value) found[key] = value.slice(0, 160);
  }
  return found;
}

function persist(next) {
  memoryStore = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage non scrivibile: l'attribution vive solo in memoryStore.
  }
}

function toTouch(found, pathname, capturedAt) {
  return {
    ...found,
    landing_page: typeof pathname === "string" ? pathname.slice(0, 500) : "/",
    captured_at: capturedAt,
  };
}

function filterTouchForConsent(touch, consent) {
  const filtered = { ...touch };
  if (!consent.marketing) {
    CLICK_ID_PARAMS.forEach((key) => delete filtered[key]);
  }
  return filtered;
}

function filterStoredForConsent(stored, consent) {
  const next = { ...stored };
  if (next.first_touch) next.first_touch = filterTouchForConsent(next.first_touch, consent);
  if (next.last_non_direct_touch) {
    next.last_non_direct_touch = filterTouchForConsent(next.last_non_direct_touch, consent);
  }
  return next;
}

// Cattura first touch e ultimo touch non diretto come blocchi indipendenti.
// Un nuovo touch non eredita click ID o UTM mancanti dal precedente.
export function captureAttribution() {
  if (!isBrowser()) return;
  const consent = getConsentState();
  // Do not mutate or create attribution storage until the CMP has initialized.
  // Once a known choice denies both purposes, remove any previous attribution.
  if (!consent.known) return;
  if (!consent.analytics && !consent.marketing) {
    clearAttribution();
    return;
  }
  const stored = filterStoredForConsent(readStored(), consent);
  const now = Date.now();
  const found = collectCampaignParams(window.location.search, {
    allowAnalytics: consent.analytics,
    allowMarketing: consent.marketing,
  });
  const next = {
    ...stored,
    schema_version: SCHEMA_VERSION,
    captured_at: now,
    landing_page: stored.landing_page || window.location.pathname,
  };

  if (Object.keys(found).length > 0) {
    const touch = toTouch(found, window.location.pathname, now);
    if (!stored.first_touch) next.first_touch = touch;
    next.last_non_direct_touch = touch;
  }

  persist(next);
}

export function clearAttribution() {
  memoryStore = {};
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage non scrivibile: la memoria viene comunque pulita.
  }
}

export function getAttribution() {
  const consent = getConsentState();
  if (!consent.known || (!consent.analytics && !consent.marketing)) return {};
  const stored = readStored();
  const activeTouch = stored.last_non_direct_touch || stored.first_touch || {};
  const { captured_at: _capturedAt, ...legacyTouch } = activeTouch;
  const firstTouch = stored.first_touch
    ? filterTouchForConsent(stored.first_touch, consent)
    : undefined;
  const lastTouch = stored.last_non_direct_touch
    ? filterTouchForConsent(stored.last_non_direct_touch, consent)
    : undefined;
  return {
    schema_version: stored.schema_version,
    ...(consent.analytics || consent.marketing ? filterTouchForConsent(legacyTouch, consent) : {}),
    ...(firstTouch ? { first_touch: firstTouch } : {}),
    ...(lastTouch ? { last_non_direct_touch: lastTouch } : {}),
    landing_page: stored.landing_page,
  };
}
